// =================================================================================
// File: src/components/Screens/MudoMapScreen.jsx
// Description: Global Mudo Map (Leaflet) showing all public studios
// VISITOR WORLD: Clicking pins navigates to studio page using slug or id
// =================================================================================

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePublicStudio } from "../../contexts/PublicStudioContext";
import { Globe, Search, X } from "lucide-react";
import MudoWorldMap from "../Map/MudoWorldMap";
import {
  getNominatimHeaders,
  applyNominatimContact,
} from "../../utils/nominatim";

export default function MudoMapScreen({ hideHeader = false }) {
  const navigate = useNavigate();
  const { allPublicStudios = [], loading } = usePublicStudio() || {};

  const mapRef = useRef(null);
  const searchBoxRef = useRef(null);
  const isDev = import.meta?.env?.DEV;

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchStatus, setSearchStatus] = useState("idle");
  const [searchError, setSearchError] = useState(null);

  const nominatimLastAtRef = useRef(0);
  const nomHeaders = getNominatimHeaders();
  const nomFetch = async (url, { retry = true } = {}) => {
    const wait = Math.max(0, 1100 - (Date.now() - nominatimLastAtRef.current));
    if (wait > 0) await new Promise((r) => setTimeout(r, wait));
    nominatimLastAtRef.current = Date.now();
    const resp = await fetch(url, { headers: nomHeaders });
    if ((resp.status === 429 || resp.status === 503) && retry) {
      await new Promise((r) => setTimeout(r, 1300));
      nominatimLastAtRef.current = Date.now();
      return nomFetch(url, { retry: false });
    }
    return resp;
  };

  // Convert studios to map location format
  const mapLocations = allPublicStudios
    .filter((studio) => studio.lat && studio.lng) // Only include studios with coordinates
    .map((studio) => ({
      id: studio.studio_id || studio.id,
      name: studio.display_name || studio.name || "Studio",
      type: "studio",
      lat: studio.lat,
      lng: studio.lng,
      slug: studio.slug,
      website_url: studio.website,
    }));

  // Debug logging
  console.log("[MudoMapScreen] Studios from context:", allPublicStudios.length);
  console.log("[MudoMapScreen] Map locations:", mapLocations.length);

  // Handle location click from map (pin click)
  const handleLocationClick = (location) => {
    if (isDev) console.log("[MudoMapScreen] handleLocationClick:", location);

    // In visitor world, navigate to studio page using slug or id
    const identifier = location.slug || location.id;
    if (!identifier) {
      console.warn("[MudoMapScreen] Missing studio id/slug for navigation:", location);
      return;
    }

    const target = `/discover/studios/${encodeURIComponent(String(identifier))}`;
    console.log("[MudoMapScreen] Navigating to:", target);
    navigate(target, { state: { studio: location, source: "mudo-map-pin" } });
  };

  // Search location handler
  const handleSearch = async (query) => {
    try {
      const trimmed = query.trim();
      if (!trimmed) {
        setSearchStatus("idle");
        setSearchError(null);
        setSearchResults([]);
        return;
      }

      setSearchStatus("loading");
      setSearchError(null);

      const url = applyNominatimContact(
        new URL("https://nominatim.openstreetmap.org/search")
      );
      url.searchParams.set("q", trimmed);
      url.searchParams.set("format", "json");
      url.searchParams.set("limit", "5");
      const resp = await nomFetch(url.toString());
      const data = await resp.json();
      setSearchResults(data || []);
      setSearchStatus("idle");
    } catch (err) {
      console.error("Search failed:", err);
      setSearchStatus("error");
      setSearchError("Search unavailable. Please try again.");
      setSearchResults([]);
    }
  };

  // Debounce location search input
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchStatus("idle");
      setSearchError(null);
      setSearchResults([]);
      return;
    }

    const t = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const zoomFromBoundingBox = (boundingbox) => {
    // boundingbox: [south, north, west, east] as strings
    if (!Array.isArray(boundingbox) || boundingbox.length < 4) return 11;
    const south = Number(boundingbox[0]);
    const north = Number(boundingbox[1]);
    if (!Number.isFinite(south) || !Number.isFinite(north)) return 11;
    const span = Math.abs(north - south);
    if (span > 20) return 3;
    if (span > 10) return 4;
    if (span > 5) return 5;
    if (span > 2) return 6;
    if (span > 1) return 7;
    if (span > 0.5) return 8;
    if (span > 0.2) return 9;
    if (span > 0.1) return 10;
    if (span > 0.05) return 11;
    return 13;
  };

  const handleSelectSearchResult = (result) => {
    const lat = Number(result?.lat);
    const lng = Number(result?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setSearchStatus("error");
      setSearchError("Couldn't locate that place.");
      return;
    }

    const zoom = zoomFromBoundingBox(result?.boundingbox);

    const map = mapRef.current;
    if (map) {
      map.flyTo([lat, lng], zoom, { animate: true, duration: 1.25 });
    }

    setSearchQuery(result?.display_name || "");
    setSearchResults([]);
  };

  // Close search dropdown on outside click / ESC
  useEffect(() => {
    const onDocClick = (e) => {
      if (!searchBoxRef.current) return;
      if (!searchBoxRef.current.contains(e.target)) {
        setSearchResults([]);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSearchQuery("");
        setSearchResults([]);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Count studios missing coordinates
  const missingCoords = allPublicStudios.length - mapLocations.length;

  return (
    <div className="flex flex-col h-screen bg-zinc-950 text-white">
      {!hideHeader && (
        <div className="sticky top-0 z-20 flex items-center gap-3 p-4 border-b border-white/10 bg-zinc-900/70 backdrop-blur">
          <Globe className="w-6 h-6 text-blue-500" />
          <div className="flex-1">
            <h1 className="text-xl font-semibold">Mudo Map</h1>
            <p className="text-sm opacity-70">
              Showing {mapLocations.length} of {allPublicStudios.length} ceramic studios
              {missingCoords > 0 && ` (${missingCoords} missing location data)`}
            </p>
          </div>

          {/* Search Location */}
          <div className="relative w-64" ref={searchBoxRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-50" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              placeholder="Search location..."
              className="w-full pl-9 pr-9 py-2 rounded-xl bg-white/5 border border-white/10 text-sm focus:outline-none focus:border-blue-500/50 focus:bg-white/10"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSearchStatus("idle");
                  setSearchError(null);
                  setSearchResults([]);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {(searchResults.length > 0 || searchStatus === "loading" || searchStatus === "error") && (
            <div className="absolute top-full mt-2 w-full bg-zinc-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
              {searchStatus === "loading" && (
                <div className="px-4 py-3 text-sm text-white/70">Searching…</div>
              )}
              {searchStatus === "error" && (
                <div className="px-4 py-3 text-sm text-red-300">
                  {searchError || "Search failed."}
                </div>
              )}
              {searchStatus !== "loading" && searchStatus !== "error" && searchResults.length === 0 && (
                <div className="px-4 py-3 text-sm text-white/70">No results</div>
              )}
              {searchResults.map((result, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSelectSearchResult(result)}
                  className="w-full px-4 py-3 text-left hover:bg-white/5 border-b border-white/5 last:border-b-0"
                >
                  <div className="text-sm font-medium">{result.display_name}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      <div className="relative flex-1" style={{ pointerEvents: "auto" }}>
        {/* overlay must NOT capture clicks - ensure loading overlay doesn't block */}
        {loading && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-zinc-950/80"
            style={{ pointerEvents: "none" }}
          >
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm opacity-70">Loading studios...</p>
            </div>
          </div>
        )}

        {/* map MUST capture clicks - ensure it's in front and unblocked */}
        <div
          className="absolute inset-0"
          data-map-container
          style={{ 
            pointerEvents: "auto", 
            zIndex: 1, 
            position: "absolute", 
            height: "100%",
            width: "100%",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
          }}
        >
          <MudoWorldMap
            locations={mapLocations}
            initialCenter={[20, 0]}
            initialZoom={2}
            onLocationClick={handleLocationClick}
            worldMode="visitor"
            onMapCreated={(map) => {
              mapRef.current = map;
            }}
          />
        </div>
      </div>
    </div>
  );
}
